import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getAdminSession, getAdminSessions, updateSubstitutionAsAdmin } from '@/lib/api-calls';
import { AdminSession } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

interface CancelSessionSubstituteByAdminProps {
  session: AdminSession;
  subRequest: AdminSession['substitutionRequests'][number];
  trigger: ReactNode;
}

export const CancelSessionSubstituteByAdmin = ({ subRequest, trigger }: CancelSessionSubstituteByAdminProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => updateSubstitutionAsAdmin({ subRequestId: subRequest.id, data: { status: 'cancelled' } }),
  });

  const onSubmit = () => {
    return mutateAsync()
      .then(() => {
        toast({
          title: 'Sub-request canceled',
          description:
            'This substitution request has been canceled. The original teacher will be the teacher for this class.',
        });
        queryClient.invalidateQueries(getAdminSessions());
        queryClient.invalidateQueries(getAdminSession(subRequest.sessionId));
        setOpen(false);
      })
      .catch(() => {
        toast({
          title: 'Sub-request failed to cancel',
        });
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel substitution request</AlertDialogTitle>
          <AlertDialogDescription>
            You are canceling an open substitution for this session.
            <p className="mt-2">
              Once canceled, the original coach: <b>{subRequest.requestedByCoach.fullNameOrEmail}</b> will still be the
              teacher for this class. Please double check the teacher's availability before canceling.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              Cancel substitute request
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
